import { useAppDispatch } from "src/common/state/hooks";
import { useSelector } from "react-redux";

import { fetchFilteredMedias, getMediaSearch, setMediaSearch } from "./state/gallerySlice";
import { MEDIA_LIST_COMPONENT_ID } from "./Gallery";
import { PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID } from "../projectGroups/components/ProjectGroupList";
import {
  fetchProjectGroupsWithProjects,
  getSearchState,
  setSearchPage,
} from "../projectGroups/state/projectGroupsSlice";

interface UseGalleryPaginationArgs {
  componentId?: string;
  mode?: "default" | "projectGroup" | "projectGroupMedias" | "projectMedias";
}

interface PageChangeArgs {
  newPage?: number;
  newLimit?: number;
  newSort?: string;
  newSelectedId?: number;
  projectGroupId?: number;
  projectId?: number;
  extraParams?: Record<string, any>;
}

export const useGalleryPagination = ({
  mode = "default",
  componentId,
}: UseGalleryPaginationArgs = {}) => {
  const dispatch = useAppDispatch();

  const mediaSearch = useSelector(getMediaSearch);
  const projectGroupSearch = useSelector(getSearchState);

  const currentLimit = mode === "projectGroup" ? projectGroupSearch.limit : mediaSearch.limit;
  const currentSort = mode === "projectGroup" ? projectGroupSearch.sort : mediaSearch.sort;

  const currentSelectedId =
    mode === "projectGroup"
      ? (projectGroupSearch.selectedId as number | undefined)
      : (mediaSearch.selectedId as number | undefined);

  const resolvedComponentId =
    componentId ??
    (mode === "projectGroup"
      ? PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID
      : MEDIA_LIST_COMPONENT_ID);

  return ({
    newPage = 0,
    newLimit,
    newSort,
    newSelectedId,
    projectGroupId,
    extraParams = {},
  }: PageChangeArgs) => {
    const finalLimit = newLimit ?? currentLimit;
    const finalSort = newSort ?? (currentSort && currentSort !== "0" ? currentSort : undefined);
    const start = newPage * finalLimit;

    if (mode === "default") {
      const finalSelectedId =
        newSelectedId ?? (currentSelectedId != null ? Number(currentSelectedId) : undefined);

      dispatch(setMediaSearch({ page: newPage }));
      dispatch(
        fetchFilteredMedias({
          start,
          limit: finalLimit,
          sort: finalSort,
          selectedId: finalSelectedId,
          ...(extraParams ?? {}),
          componentId: resolvedComponentId,
        }),
      );
      return;
    }

    if (mode === "projectGroup") {
      const finalProjectGroupId =
        projectGroupId ??
        (extraParams?.projectGroupId as number | undefined) ??
        (currentSelectedId as number | undefined);

      dispatch(setSearchPage(newPage));
      dispatch(
        fetchProjectGroupsWithProjects({
          start,
          limit: finalLimit,
          sort: finalSort,
          ...(extraParams ?? {}),
          projectGroupId: finalProjectGroupId,
          componentId: resolvedComponentId,
        }),
      );
      return;
    }

    if (mode === "projectGroupMedias") {
      const finalSelectedId =
        newSelectedId ?? (currentSelectedId != null ? Number(currentSelectedId) : undefined);

      const finalProjectGroupId =
        projectGroupId ?? (extraParams?.projectGroupId as number | undefined);

      dispatch(setMediaSearch({ page: newPage }));
      dispatch(
        fetchFilteredMedias({
          start,
          limit: finalLimit,
          sort: finalSort,
          selectedId: finalSelectedId,
          ...(extraParams ?? {}),
          projectGroupId: finalProjectGroupId,
          componentId: resolvedComponentId,
        }),
      );
      return;
    }
    if (mode === "projectMedias") {
      const finalSelectedId =
        newSelectedId ?? (currentSelectedId != null ? Number(currentSelectedId) : undefined);

      const finalProjectId = projectGroupId ?? (extraParams?.projectId as number | undefined);

      dispatch(setMediaSearch({ page: newPage }));
      dispatch(
        fetchFilteredMedias({
          start,
          limit: finalLimit,
          sort: finalSort,
          selectedId: finalSelectedId,
          ...(extraParams ?? {}),
          projectGroupId: finalProjectId,
          componentId: resolvedComponentId,
        }),
      );
      return;
    }
  };
};

export type { PageChangeArgs };
